import { waitForAmplitude } from '@theme/utilities';
import { getPageGlobalProps } from '@theme/analytics.util';

/**
 * Tracks Amplitude CTA events for Split Content buttons rendered from the
 * split_content_section metaobject (see custom-page-split-content.liquid).
 *
 * Buttons are plain links, so navigation is held until the event is delivered
 * (capped by NAVIGATION_TIMEOUT_MS) to keep page unload from dropping it.
 */
const namespace = '[CustomPageCtaAnalytics]';
const LINK_SELECTOR = '.custom-page-split a[data-analytics-event]';
const NAVIGATION_TIMEOUT_MS = 1000;

/**
 * Tracks the link's data-analytics-event with the same properties bodi-button.js sends.
 *
 * @param {HTMLAnchorElement} link
 * @returns {Promise<{ promise: Promise<unknown> } | undefined>} Amplitude's flush() result, whose
 *   `promise` settles once the queued events are sent.
 */
const trackCtaClick = async (link) => {
  const analyticsEvent = link.dataset.analyticsEvent;
  if (!analyticsEvent || analyticsEvent === 'none') return;

  const eventName = analyticsEvent.replace(/\s+/g, ' ').toLowerCase();
  const payload = {
    module: link.dataset.analyticsModule || 'unknown',
    'cta url': link.getAttribute('href') || link.href,
  };

  try {
    await waitForAmplitude();

    const eventProperties = {
      ...getPageGlobalProps(window.location.href),
      ...payload,
    };

    window.amplitude.track(eventName, eventProperties);
    const flushResult = window.amplitude.flush();
    console.info(`${namespace} - Tracked ${eventName}`, eventProperties);
    return flushResult;
  } catch (error) {
    console.error(`${namespace} - Failed to track ${eventName}`, { error, payload });
  }
};

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {MouseEvent} event
 * @param {HTMLAnchorElement} link
 * @returns {boolean} True when the browser should keep handling the click itself.
 */
const shouldLeaveNavigationToBrowser = (event, link) =>
  event.defaultPrevented ||
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey ||
  (link.target !== '' && link.target !== '_self') ||
  link.hasAttribute('download');

/**
 * @param {MouseEvent} event
 */
const handleClick = async (event) => {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest(LINK_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return;

  if (link.dataset.analyticsPending === 'true') {
    event.preventDefault();
    return;
  }

  const tracking = trackCtaClick(link);

  // Clicks already handled elsewhere (e.g. custom-product-section-rendering scrolling to
  // an on-page product) or opening a new tab only need the event sent.
  if (shouldLeaveNavigationToBrowser(event, link)) return;

  event.preventDefault();
  link.dataset.analyticsPending = 'true';

  const delivery = tracking.then((flushResult) => flushResult?.promise).catch(() => {});
  await Promise.race([delivery, wait(NAVIGATION_TIMEOUT_MS)]);
  window.location.assign(link.href);
};

// Listening on window (bubble phase) runs after document-level click handlers, so
// event.defaultPrevented reflects their decisions.
window.addEventListener('click', handleClick);

// The pending flag stays set until the page unloads; clear it if the page is restored
// from the back/forward cache so the link works again.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  document.querySelectorAll(`${LINK_SELECTOR}[data-analytics-pending]`).forEach((link) => {
    if (link instanceof HTMLElement) delete link.dataset.analyticsPending;
  });
});
